import { makeStyles } from "@mui/styles";
import React, { PropsWithChildren } from "react";
import QACheckbox from "../Design/Controls/QACheckbox";
import SettingsTextField from "../Settings/Old/Form/SettingsTextField";
import QAButtonRow from "../Design/Layout/QAButtonRow";
import QAButton from "../Design/Controls/QAButton";
import SettingsSelect from "../Settings/Old/Form/SettingsSelect";
import { MenuItem } from "@mui/material";

export interface VariableDefinition {
    name: string;
    type: "STRING" | "NUMBER" | "BOOLEAN" | "SECRET";
    required: boolean;
    defaultValue?: string;
}

interface Props {
    variables: VariableDefinition[];
    onUpdate: (variables: VariableDefinition[]) => void;
    disabled?: boolean;
}

const useStyles = makeStyles({
    root: {
        display: "flex",
        flexDirection: "column"
    },
    variables: {
        border: "1px solid #CCC",
        borderRadius: "4px",
        padding: "1rem",
        display: "flex",
        flexDirection: "column",
        marginBottom: "1rem"
    },
    variable: {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        gap: "0.5rem",
        "&>*": {
            margin: 0
        }
    },
    variableName: {
        flex: 1
    },
    variableType: {
        width: "120px"
    },
    variableDefault: {
        flex: 1
    },
    variableRequired: {
        width: "120px"
    }
});

const SettingsForm: React.FC<PropsWithChildren<Props>> = props => {
    const classes = useStyles();

    const { variables, disabled, onUpdate } = props;

    return (
        <div className={classes.root}>

            <QAButtonRow marginBottom alignRight>

                <QAButton
                    light
                    size="tiny"
                    disabled={disabled}
                    title="Hinzufügen"
                    onClicked={() => onUpdate([
                        ...variables,
                        {
                            name: "",
                            defaultValue: "",
                            required: false,
                            type: "STRING"
                        }
                    ])} />

            </QAButtonRow>

            <div className={classes.variables}>

                {variables.length === 0 && (
                    <span>Keine Variablen vorhanden.</span>
                )}

                {variables.map((variable, index) => (
                    <div
                        className={classes.variable}
                        key={index}>

                        <SettingsTextField
                            label="Name"
                            disabled={disabled}
                            className={classes.variableName}
                            onChanged={value => onUpdate(variables.map(v => v === variable ? {
                                ...v,
                                // Only Java variable syntax allowed
                                name: value.replaceAll(/[^a-zA-Z0-9_]/g, "")
                            } : v))}
                            value={variable.name} />

                        <SettingsSelect
                            value={variable.type}
                            label="Typ"
                            className={classes.variableType}
                            onChanged={value => onUpdate(variables.map(v => v === variable ? {
                                ...v,
                                type: value
                            } : v))}>
                            <MenuItem value="STRING">String</MenuItem>
                            <MenuItem value="NUMBER">Number</MenuItem>
                            <MenuItem value="BOOLEAN">Boolean</MenuItem>
                            <MenuItem value="SECRET">Secret</MenuItem>
                        </SettingsSelect>

                        {variable.type !== "BOOLEAN" && (
                            <SettingsTextField
                                label="Default-Wert"
                                className={classes.variableDefault}
                                type={variable.type === "NUMBER" ? "number" : (variable.type === "SECRET" ? "password" : "text")}
                                disabled={disabled}
                                onChanged={value => onUpdate(variables.map(v => v === variable ? {
                                    ...v,
                                    defaultValue: value
                                } : v))}
                                value={variable.defaultValue ?? ""} />
                        )}

                        {variable.type === "BOOLEAN" && (
                            <QACheckbox
                                label="Default-Wert"
                                disabled={disabled}
                                className={classes.variableRequired}
                                onChanged={checked => onUpdate(variables.map(v => v === variable ? {
                                    ...v,
                                    defaultValue: String(checked)
                                } : v))}
                                checked={variable.defaultValue === "true"} />
                        )}

                        <QACheckbox
                            label="Pflichtfeld"
                            disabled={disabled}
                            onChanged={checked => onUpdate(variables.map(v => v === variable ? {
                                ...v,
                                required: checked
                            } : v))}
                            checked={variable.required} />
                    </div>
                ))}

            </div>
        </div>
    );
};

export default SettingsForm;
